import { generateUuid } from "../Services/UuidGenerator";

export interface Notification {
    name: string;
    body: any;
}

export class Subscriber {
    private standardQueue: Notification[] = [];

    private interests: Map<string, (event: any) => void> = new Map();
    private key: string;
    private shouldPost = false;

    constructor(key: string = "" + new Date().getTime()) {
        this.key = key;
    }

    public on(eventName: string, callback: (event: any) => void): void {
        this.interests.set(eventName, callback);
    }

    public off(eventName: string): void {
        this.interests.delete(eventName);
    }

    public getKey(): string {
        return this.key;
    }

    public start(): void {
        this.shouldPost = true;
        this.processNotifications();
    }

    public pause(): void {
        this.shouldPost = false;
    }

    public post(notification: Notification): void {
        this.standardQueue.unshift(notification);

        this.processNotifications();
    }

    private processNotifications(): void {
        if (!this.shouldPost) {
            return;
        }

        this.postNotifications(this.standardQueue);
    }

    private postNotifications(queue: Notification[]): void {
        while (queue.length > 0) {
            const notification: Notification = queue.shift() as Notification;
            this.postCallback(notification);
        }
    }

    private postCallback(notification: Notification): void {
        const callback = this.interests.get(notification.name);

        if (callback) {
            callback.call(this, notification);
        }
    }
}

export class Publisher {
    private subscribers: Map<string, Subscriber> = new Map();

    public add(subscriber: Subscriber): void {
        const key: string = subscriber.getKey();

        if (!this.has(key)) {
            this.subscribers.set(key, subscriber);
        }
    }

    public delete(subscriber: Subscriber): void {
        this.subscribers.delete(subscriber.getKey());
    }

    public has(key: string): boolean {
        return this.subscribers.has(key);
    }

    public notify(eventName: string, data: any): void {
        this.sendNotification(eventName, data);
    }

    private sendNotification(eventName: string, data: any): void {
        this.subscribers.forEach((subscriber) => {
            subscriber.post({
                name: eventName,
                body: data,
            });
        });
    }
}

export type BaseEvent = {
    name: string;
    data?: object;
};

export const publisher = new Publisher();

export const publish = (event: BaseEvent) => {
    publisher.notify(event.name, event);
};

export const addSubscriber = (type: string, callback: (event: any) => void) => {
    const subscriber = new Subscriber(generateUuid());
    publisher.add(subscriber);
    subscriber.on(type, callback);
    subscriber.start();
    return subscriber;
};
export const removeSubscriber = (subscriber: Subscriber) => {
    publisher.delete(subscriber);
};
